import { AgxToast } from '@urbanx/agx-ui-components';
import { useInvalidateUseGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client';
import {
  useStaffGetLaunchDateDetailsForCampaign,
  CampaignDetailModel,
  LaunchDateDetails,
  useStaffUpdateLaunchDateForCampaign,
  useInvalidateUseStaffGetLaunchDateDetailsForCampaign,
  StaffUpdateLaunchDateForCampaignRequestStatus,
  LaunchDateDetailsStatus,
} from '@urbanx/agx.campaigns.client';
import {
  AgxColumn,
  AgxSpacer,
  AgxTypography,
  AgxLaunchDateDetails,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgxToastState } from 'types/commonTypes';
import { isEqual } from 'lodash-es';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';

interface LaunchDateDetailsProps {
  campaign?: CampaignDetailModel;
  onSaveStateChange: (state: {
    saveChanges: (() => void) | undefined;
    isFormDirty: boolean;
    isFormValid: boolean;
    isPending: boolean;
    showSavedMessage: boolean;
  }) => void;
}

const isLaunchFormValid = (details: LaunchDateDetails | null) => {
  if (!details?.status) return false;

  if (details.status === LaunchDateDetailsStatus.Confirmed) {
    return details.launchDate !== undefined;
  }

  return (
    details.status === LaunchDateDetailsStatus.ToBeConfirmed ||
    details.status === LaunchDateDetailsStatus.OffMarket
  );
};

const normalizeLaunchDateDetails = (details: LaunchDateDetails): LaunchDateDetails => {
  const status = details.status ?? LaunchDateDetailsStatus.Confirmed;

  return {
    ...details,
    status,
    launchDate:
      status === LaunchDateDetailsStatus.ToBeConfirmed ||
      status === LaunchDateDetailsStatus.OffMarket
        ? undefined
        : details.launchDate,
  };
};

const Launch = ({ campaign, onSaveStateChange }: LaunchDateDetailsProps) => {
  const { data: launchDateInfo, isPending: launchDateInfoPending } =
    useStaffGetLaunchDateDetailsForCampaign(campaign?.campaignId || '', !!campaign);
  const initialLoadDoneRef = useRef(false);
  const initialLaunchDateDetailsRef = useRef<LaunchDateDetails | null>(null);
  const [launchDateDetails, setLaunchDateDetails] = useState<LaunchDateDetails | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const launchDateDetailsRef = useRef<LaunchDateDetails | null>(null);
  const invalidateStaffGetLaunchDateDetails =
    useInvalidateUseStaffGetLaunchDateDetailsForCampaign();
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();

  useEffect(() => {
    initialLoadDoneRef.current = false;
    initialLaunchDateDetailsRef.current = null;
    launchDateDetailsRef.current = null;
    setLaunchDateDetails(null);
    setIsFormDirty(false);
    setShowSavedMessage(false);
  }, [campaign?.campaignId]);

  useEffect(() => {
    if (!launchDateInfo) return;
    if (initialLoadDoneRef.current) return;

    const initialLaunchDateDetails = normalizeLaunchDateDetails(launchDateInfo);

    setLaunchDateDetails(initialLaunchDateDetails);
    initialLaunchDateDetailsRef.current = initialLaunchDateDetails;
    launchDateDetailsRef.current = initialLaunchDateDetails;
    setIsFormDirty(false);
    initialLoadDoneRef.current = true;
  }, [launchDateInfo]);

  useEffect(() => {
    launchDateDetailsRef.current = launchDateDetails;
  }, [launchDateDetails]);

  const isFormValid = isLaunchFormValid(launchDateDetails);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const refreshTasksAfterLaunchDateUpdate = async (campaignId: string) => {
    await invalidateTasks(campaignId);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await invalidateTasks(campaignId);
  };

  const { staffUpdateLaunchDateForCampaign } = useStaffUpdateLaunchDateForCampaign({
    onSuccess: async () => {
      const campaignId = campaign?.campaignId;

      if (campaignId) {
        await invalidateStaffGetLaunchDateDetails(campaignId);
        await refreshTasksAfterLaunchDateUpdate(campaignId);
      }

      initialLoadDoneRef.current = false;
      setIsFormDirty(false);
      setShowSavedMessage(true);

      updateToastState({
        color: 'success',
        message: 'Launch date details updated successfully',
        open: true,
      });
    },
    onError: (error) => {
      console.error('Error updating launch date details:', error);
      updateToastState({
        color: 'error',
        message: 'Error updating launch date details',
        open: true,
      });
    },
  });

  const saveChanges = useCallback(() => {
    if (!campaign?.campaignId) return;

    const currentLaunchDateDetails = launchDateDetailsRef.current;

    if (!currentLaunchDateDetails) {
      updateToastState({
        color: 'error',
        message: 'No launch details to save',
        open: true,
      });
      return;
    }

    if (!isLaunchFormValid(currentLaunchDateDetails)) {
      updateToastState({
        color: 'error',
        message: 'Please complete launch date details before saving',
        open: true,
      });
      return;
    }

    staffUpdateLaunchDateForCampaign({
      campaignId: campaign.campaignId,
      ...currentLaunchDateDetails,
      launchDate: toDateOnlyString(currentLaunchDateDetails.launchDate),
      status:
        currentLaunchDateDetails.status as unknown as StaffUpdateLaunchDateForCampaignRequestStatus,
    });

    setShowSavedMessage(true);
    setIsFormDirty(false);
  }, [campaign?.campaignId, staffUpdateLaunchDateForCampaign]);

  useEffect(() => {
    onSaveStateChange({
      saveChanges: campaign?.campaignId ? saveChanges : undefined,
      isFormDirty: isFormDirty,
      isFormValid,
      isPending: launchDateInfoPending,
      showSavedMessage,
    });
  }, [
    campaign?.campaignId,
    saveChanges,
    isFormDirty,
    isFormValid,
    launchDateInfoPending,
    showSavedMessage,
    onSaveStateChange,
  ]);

  return (
    <>
      <AgxToast selector="#agxToast" toastState={toastState} />

      <AgxSpacer direction="vertical" gap="spacing-2xl" padding={{ top: 'spacing-3xl' }} fill>
        <Panel
          title={
            <AgxTypography id="panel" type="title" size="large">
              Launch
            </AgxTypography>
          }
        >
          <AgxColumn gap="spacing-3xl" fill padding="spacing-3xl">
            {!launchDateInfoPending && launchDateDetails && (
              <AgxLaunchDateDetails
                id="launch-date-details"
                validate
                defaultValue={launchDateDetails}
                onValueChanged={({ value }) => {
                  const mergedValue = {
                    ...launchDateDetails,
                    ...value,
                  } as LaunchDateDetails;
                  const normalizedValue = normalizeLaunchDateDetails(mergedValue);

                  const hasChanged = !isEqual(launchDateDetailsRef.current, normalizedValue);
                  if (!hasChanged) return;

                  setLaunchDateDetails(normalizedValue);
                  setIsFormDirty(!isEqual(initialLaunchDateDetailsRef.current, normalizedValue));
                  setShowSavedMessage(false);
                }}
              />
            )}
          </AgxColumn>
        </Panel>
      </AgxSpacer>
    </>
  );
};

export default Launch;
